import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Collapse,
  Row,
  Col,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, GlobalOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import { getCategoryList, createCategory, updateCategory, deleteCategory } from '../services/api';

const { Panel } = Collapse;

const CategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [langModalVisible, setLangModalVisible] = useState(false);
  const [langModalData, setLangModalData] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await getCategoryList();
      setCategories(data || []);
    } catch (error) {
      console.error('加载分类失败', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    form.setFieldsValue({ sort_order: 0, status: true });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCategory(record);
    form.setFieldsValue({
      ...record,
      status: record.status === 1,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCategory(id);
      message.success('分类已删除');
      loadCategories();
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        status: values.status ? 1 : 0,
      };

      if (editingCategory) {
        await updateCategory(editingCategory.id, data);
        message.success('分类已更新');
      } else {
        await createCategory(data);
        message.success('分类已创建');
      }

      setModalVisible(false);
      loadCategories();
    } catch (error) {
      console.error('保存失败', error);
    }
  };

  const showLangModal = (record) => {
    setLangModalData(record);
    setLangModalVisible(true);
  };

  const columns = [
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 70,
    },
    {
      title: '分类标识',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '中文',
      dataIndex: 'label_zh',
      key: 'label_zh',
      width: 100,
      render: (text) => text || '-',
    },
    {
      title: '英文',
      dataIndex: 'label_en',
      key: 'label_en',
      width: 100,
      render: (text) => text || '-',
    },
    {
      title: '多语言',
      key: 'lang',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<GlobalOutlined />}
          onClick={() => showLangModal(record)}
        >
          查看
        </Button>
      ),
    },
    {
      title: '贴纸数量',
      dataIndex: 'sticker_count',
      key: 'sticker_count',
      width: 100,
      render: (count) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除该分类吗？"
            description="删除后该分类下的贴纸将失去此分类"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>分类管理</h2>
        <p>管理贴纸分类，支持多语言标签</p>
      </div>

      <Card bordered={false}>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加分类
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      {/* 多语言查看弹窗 */}
      <Modal
        title="多语言标签"
        open={langModalVisible}
        onCancel={() => setLangModalVisible(false)}
        footer={null}
        width={400}
      >
        {langModalData && (
          <div style={{ lineHeight: 2.2 }}>
            <div><strong>繁体中文：</strong>{langModalData.label_zh_tw || '-'}</div>
            <div><strong>日文：</strong>{langModalData.label_ja || '-'}</div>
            <div><strong>韩文：</strong>{langModalData.label_ko || '-'}</div>
          </div>
        )}
      </Modal>

      {/* 添加/编辑分类弹窗 */}
      <Modal
        title={editingCategory ? '编辑分类' : '添加分类'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="保存"
        cancelText="取消"
        width={700}
      >
        <Form form={form} layout="vertical">
          {/* 基础信息区域 */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: '#333' }}>基础信息</div>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="分类标识"
                  rules={[{ required: true, message: '请输入分类标识' }]}
                  extra="内部标识，建议使用英文"
                >
                  <Input placeholder="如: emoji, animals" maxLength={100} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="sort_order" label="排序权重" extra="数值越大越靠前">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="status" label="状态" valuePropName="checked">
                  <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="icon" label="图标URL">
                  <Input placeholder="请输入图标URL（可选）" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="description" label="描述">
                  <Input placeholder="请输入分类描述（可选）" maxLength={500} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* 多语言信息区域 - 可折叠 */}
          <Collapse
            defaultActiveKey={['lang']}
            expandIcon={({ isActive }) => isActive ? <DownOutlined /> : <RightOutlined />}
          >
            <Panel header={<span style={{ fontWeight: 500 }}>多语言标签</span>} key="lang">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="label_zh" label="简体中文">
                    <Input placeholder="简体中文标签" maxLength={100} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="label_en" label="英文">
                    <Input placeholder="English label" maxLength={100} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="label_zh_tw" label="繁体中文">
                    <Input placeholder="繁體中文標籤" maxLength={100} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="label_ja" label="日文">
                    <Input placeholder="日本語ラベル" maxLength={100} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="label_ko" label="韩文">
                    <Input placeholder="한국어 레이블" maxLength={100} />
                  </Form.Item>
                </Col>
                <Col span={8} />
              </Row>
            </Panel>
          </Collapse>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryList;
